import { useEffect, useRef, useState } from "react";

/**
 * Owns the WYSIWYG canvas interaction shared by BadgeBuilder and PDFBuilder:
 * scale-to-fit, pointer drag, and corner resize. Items are positioned with
 * percentage geometry ({ id, xPct, yPct, widthPct, heightPct }); the caller
 * keeps the items in its own state and passes `items` + `setItems`.
 */
export function useCanvasEditor({
  items,
  setItems,
  nativeWidth,
  nativeHeight,
}) {
  const containerRef = useRef(null);
  const paneRef = useRef(null);
  const [scale, setScale] = useState(1);

  const draggingId = useRef(null);
  const dragStart = useRef({ x: 0, y: 0, initialLeftPct: 0, initialTopPct: 0 });

  const resizingId = useRef(null);
  const resizeStart = useRef({
    x: 0,
    y: 0,
    initialWidthPct: 0,
    initialHeightPct: 0,
  });

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const availW = containerRef.current.clientWidth - 80;
      const availH = containerRef.current.clientHeight - 80;

      const scaleX = availW / nativeWidth;
      const scaleY = availH / nativeHeight;
      setScale(Math.min(scaleX, scaleY, 1));
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [nativeWidth, nativeHeight]);

  const handlePointerDown = (e, id, xPct, yPct) => {
    if (e.target.closest(".resize-handle")) return;

    draggingId.current = id;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      initialLeftPct: xPct,
      initialTopPct: yPct,
    };
    e.target.setPointerCapture(e.pointerId);
  };

  const handleResizePointerDown = (e, id, widthPct, heightPct) => {
    e.stopPropagation();
    resizingId.current = id;
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      initialWidthPct: widthPct,
      initialHeightPct: heightPct,
    };
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!paneRef.current) return;
    const rect = paneRef.current.getBoundingClientRect();

    if (draggingId.current) {
      const dxPct = ((e.clientX - dragStart.current.x) / rect.width) * 100;
      const dyPct = ((e.clientY - dragStart.current.y) / rect.height) * 100;

      const item = items.find((i) => i.id === draggingId.current);
      if (!item) return;

      let newXPct = Math.max(0, dragStart.current.initialLeftPct + dxPct);
      let newYPct = Math.max(0, dragStart.current.initialTopPct + dyPct);

      newXPct = Math.min(newXPct, 100 - item.widthPct);
      newYPct = Math.min(newYPct, 100 - item.heightPct);

      setItems((prev) =>
        prev.map((i) =>
          i.id === draggingId.current
            ? { ...i, xPct: newXPct, yPct: newYPct }
            : i
        )
      );
    } else if (resizingId.current) {
      const dxPct = ((e.clientX - resizeStart.current.x) / rect.width) * 100;
      const dyPct = ((e.clientY - resizeStart.current.y) / rect.height) * 100;

      const item = items.find((i) => i.id === resizingId.current);
      if (!item) return;

      let newWidthPct = Math.max(
        5,
        resizeStart.current.initialWidthPct + dxPct
      );
      let newHeightPct = Math.max(
        2,
        resizeStart.current.initialHeightPct + dyPct
      );

      newWidthPct = Math.min(newWidthPct, 100 - item.xPct);
      newHeightPct = Math.min(newHeightPct, 100 - item.yPct);

      setItems((prev) =>
        prev.map((i) =>
          i.id === resizingId.current
            ? { ...i, widthPct: newWidthPct, heightPct: newHeightPct }
            : i
        )
      );
    }
  };

  const handlePointerUp = (e) => {
    if (draggingId.current) {
      e.target.releasePointerCapture(e.pointerId);
      draggingId.current = null;
    }
    if (resizingId.current) {
      e.target.releasePointerCapture(e.pointerId);
      resizingId.current = null;
    }
  };

  return {
    containerRef,
    paneRef,
    scale,
    draggingId,
    handlePointerDown,
    handleResizePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}
